import { useState } from 'react';

import { Button } from '@components/button';
import { CheckboxGroup } from '@components/checkbox-group';
import { CopyText } from '@components/copy-text-field';
import { createForm, useCreateFormStore } from '@components/form';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconSSO } from '@components/icons/sso';
import { InfoBox } from '@components/info-box';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';

import { useCreateUserApp, useUpdateUserApp } from 'api/authentication/hooks';
import { UserApp } from 'api/authentication/requests';

type FormData = {
  name: string;
  description: string;
  hasFullAccess: boolean;
  scopes: string[];
};

const Form = createForm<FormData>();

type UserAppsSidebarProps = {
  userApp?: UserApp;
};

export const useUserAppsSidebar = () => useSidebar(UserAppsSidebar, { variant: 'REGULAR' });

const scopeOptions = [
  { value: 'profiles:read', label: 'viewTravellerProfiles' },
  { value: 'profiles:write', label: 'updateTravellerProfiles' },
  { value: 'lists:read', label: 'viewLists' },
  { value: 'lists:write', label: 'updateLists' },
  { value: 'agents:read', label: 'viewAiAgents' },
  { value: 'agents:write', label: 'updateAiAgents' },
  { value: 'workspaces:read', label: 'viewWorkspaces' },
  { value: 'workspaces:write', label: 'updateWorkspaces' },
  { value: 'settings:read', label: 'viewCompanySettings' },
  { value: 'settings:write', label: 'updateCompanySettings' },
] as const;

export const UserAppsSidebar = ({ userApp }: UserAppsSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const [newClientId, setNewClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const createMutation = useCreateUserApp({
    onSuccess: (data) => {
      setNewClientId(data.clientId);
      setClientSecret(data.clientSecret);
    },
  });
  const created = createMutation.isSuccess;
  const updateMutation = useUpdateUserApp({ onSuccess: hideSidebar });

  const handleSubmit = (data: FormData) => {
    if (userApp) {
      updateMutation.mutateAsync({
        clientId: userApp.clientId,
        clientName: data.name,
        description: data.description,
        hasFullAccess: data.hasFullAccess,
        scopes: data.scopes,
      });
    } else {
      createMutation.mutateAsync({
        clientName: data.name,
        description: data.description,
        hasFullAccess: data.hasFullAccess,
        scopes: data.scopes,
      });
    }
  };

  const isDirty = formStore.useIsDirty();

  const optionVariables = {
    travellerProfiles: {
      text: t('travellerProfiles'),
      font: 'semibold14',
    },
    lists: { text: t('lists'), font: 'semibold14' },
    aiAgents: { text: t('aiAgents'), font: 'semibold14' },
    workspaces: { text: t('workspaces'), font: 'semibold14' },
    companySettings: {
      text: t('companySettings'),
      font: 'semibold14',
    },
  } as const;

  return (
    <Form onSubmit={handleSubmit} store={formStore}>
      <Sidebar.SimpleHeader
        title={created ? t('userAppCredentials') : userApp ? t('editApiKey') : t('createApiKey')}
        customIcon={<IconSSO size={24} color="elementBlue100" />}
      />
      <Sidebar.Content>
        {created ? (
          <Stack space={6}>
            <Text text={t('copyCredentials')} />
            <Sidebar.Separator />
            <Stack space={4}>
              <CopyText
                copyText={t('copy')}
                copiedText={t('copied')}
                label={t('clientId')}
                value={newClientId}
              />
              <CopyText
                copyText={t('copy')}
                copiedText={t('copied')}
                label={t('clientSecret')}
                value={clientSecret}
                helpText={t('displayedOnce')}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack space={6}>
            {!userApp && (
              <>
                <InfoBox
                  text={t('personalAccessInfo')}
                  variant="indigo"
                  icon={<IconFormIndicatorInfo color="elementIndigo100" />}
                />
                <Sidebar.Separator />
              </>
            )}

            <Stack space={4}>
              {userApp && (
                <Stack space={1}>
                  <Text font="semibold14" text={t('clientId')} />
                  <Text font="regular14" text={userApp.clientId} />
                </Stack>
              )}
              <Form.TextField
                id="name"
                label={t('name')}
                placeholder={t('namePlaceholder')}
                validators={['required']}
                defaultValue={userApp?.clientName}
              />

              <Form.Textarea
                id="description"
                label={t('description')}
                placeholder={t('descriptionPlaceholder')}
                defaultValue={userApp?.description ?? ''}
              />
            </Stack>

            <Sidebar.Separator />

            <Stack space={4}>
              <Form.LabeledSwitch
                id="hasFullAccess"
                label={t('scope')}
                onText={t('fullAccess')}
                offText={t('customAccess')}
                defaultValue={userApp?.hasFullAccess ?? true}
              />

              <Form.ConditionalValue id="hasFullAccess" when={Boolean}>
                <Stack space={2}>
                  {scopeOptions.map((option) => (
                    <Text
                      key={option.value}
                      font="regular14"
                      color="textSecondary"
                      text={styledTranslate({
                        key: option.label,
                        variables: optionVariables,
                        translations: locale,
                      })}
                    />
                  ))}
                </Stack>
              </Form.ConditionalValue>

              <Form.ConditionalValue id="hasFullAccess" when={(value) => !value}>
                <Form.CheckboxGroup
                  layout="snug"
                  id="scopes"
                  label=""
                  defaultValue={userApp?.scopes ?? []}
                >
                  {scopeOptions.map((option) => (
                    <CheckboxGroup.Item
                      key={option.value}
                      label={styledTranslate({
                        key: option.label,
                        variables: optionVariables,
                        translations: locale,
                      })}
                      value={option.value}
                    />
                  ))}
                </Form.CheckboxGroup>
              </Form.ConditionalValue>
            </Stack>
          </Stack>
        )}
      </Sidebar.Content>
      <Sidebar.Footer>
        {created ? (
          <Stack space={4} orientation="horizontal" justify="flex-end">
            <Button variant="secondary" onClick={hideSidebar} text={t('close')} />
          </Stack>
        ) : (
          <Stack space={4} orientation="horizontal" justify="flex-end">
            <Button variant="secondary" onClick={hideSidebar} text={t('core.cancel')} />
            <Form.Submit
              text={userApp ? t('saveChanges') : t('createApiKey')}
              isDisabled={!isDirty}
            />
          </Stack>
        )}
      </Sidebar.Footer>
    </Form>
  );
};

export const locale = {
  en: {
    createApiKey: 'Create API key',
    editApiKey: 'Edit API key',
    saveChanges: 'Save changes',
    addUserApp: 'Add user app',
    personalAccessInfo: 'Personal access tokens are required to use the Journeys API',
    name: 'Name',
    namePlaceholder: 'Name your API key...',
    description: 'Description',
    descriptionPlaceholder: 'Purpose of the key...',
    scope: 'Scope',
    customAccess: 'Custom access',
    fullAccess: 'Full access',
    viewTravellerProfiles: 'View {travellerProfiles} (profiles:read)',
    updateTravellerProfiles: 'Update {travellerProfiles} (profiles:write)',
    viewLists: 'View {lists} (lists:read)',
    updateLists: 'Update {lists} (lists:write)',
    viewAiAgents: 'View {aiAgents} (agents:read)',
    updateAiAgents: 'Update {aiAgents} (agents:write)',
    viewWorkspaces: 'View {workspaces} & data in each workspace (workspaces:read)',
    updateWorkspaces: 'Update {workspaces} (workspaces:write)',
    viewCompanySettings: 'View {companySettings} (settings:read)',
    updateCompanySettings: 'Update {companySettings} (settings:write)',
    travellerProfiles: 'Traveller profiles',
    lists: 'Lists',
    aiAgents: 'AI Agents',
    workspaces: 'Workspaces',
    companySettings: 'Company Settings',
    userAppCredentials: 'User app credentials',
    copyCredentials:
      'Copy the credentials below and save them in a secure location. You will need them to authenticate with the external API',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    displayedOnce: 'Client secret is only displayed once',
  },
};
